import React, { useState, useEffect } from 'react';
import { Brain, BookOpen, Headphones, Play, CheckCircle, Clock, Star, Loader, Filter } from 'lucide-react';
import Layout from '../Layout';
import ParentAPI from '../services/ParentAPI';
import { showErrorNotification } from '../utils/errorHandler';

const PersonalisedLearning = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [learningData, setLearningData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('mcqs');

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadLearningData();
        }
    }, [selectedChild]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadLearningData = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);
            
            // Mock personalized learning data
            const mockLearningData = {
                mcqs: [
                    {
                        id: '1',
                        title: 'Mathematics - Number Recognition',
                        subject: 'Mathematics',
                        difficulty: 'Easy',
                        questions: 10,
                        timeLimit: '15 minutes',
                        lastScore: 85,
                        attempts: 3,
                        status: 'completed',
                        recommendedFor: 'Number skills improvement'
                    },
                    {
                        id: '2',
                        title: 'Language Arts - Vocabulary Building',
                        subject: 'Language Arts',
                        difficulty: 'Medium',
                        questions: 15,
                        timeLimit: '20 minutes',
                        lastScore: 92,
                        attempts: 2,
                        status: 'completed',
                        recommendedFor: 'Reading comprehension enhancement'
                    },
                    {
                        id: '3',
                        title: 'Science - Animal Habitats',
                        subject: 'Science',
                        difficulty: 'Easy',
                        questions: 12,
                        timeLimit: '18 minutes',
                        lastScore: null,
                        attempts: 0,
                        status: 'recommended',
                        recommendedFor: 'Environmental awareness'
                    }
                ],
                oralAssessments: [
                    {
                        id: '1',
                        title: 'Story Retelling - The Three Bears',
                        subject: 'Language Arts',
                        duration: '5 minutes',
                        lastScore: 88,
                        attempts: 2,
                        status: 'completed',
                        skills: ['Speaking', 'Memory', 'Sequencing']
                    },
                    {
                        id: '2',
                        title: 'Number Counting - 1 to 50',
                        subject: 'Mathematics',
                        duration: '3 minutes',
                        lastScore: 95,
                        attempts: 1,
                        status: 'completed',
                        skills: ['Number Recognition', 'Pronunciation']
                    },
                    {
                        id: '3',
                        title: 'Color and Shape Identification',
                        subject: 'General Knowledge',
                        duration: '4 minutes',
                        lastScore: null,
                        attempts: 0,
                        status: 'recommended',
                        skills: ['Visual Recognition', 'Vocabulary']
                    }
                ],
                recommendations: {
                    strengthAreas: ['Creative Expression', 'Physical Coordination', 'Social Skills'],
                    improvementAreas: ['Reading Fluency', 'Mathematical Reasoning', 'Attention Span'],
                    suggestedActivities: [
                        'Daily reading practice for 15 minutes',
                        'Number games and puzzles',
                        'Mindfulness and focus exercises'
                    ]
                }
            };

            setLearningData(mockLearningData);
        } catch (err) {
            console.error('Error loading learning data:', err);
            showErrorNotification('Failed to load personalized learning data');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'in-progress':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'recommended':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy':
                return 'bg-green-500';
            case 'medium':
                return 'bg-yellow-500';
            case 'hard':
                return 'bg-red-500';
            default:
                return 'bg-gray-500';
        }
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Personalised Learning">
                <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Personalised Learning</h2>
                        <p className="text-gray-600">Please wait while we fetch learning activities...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild) {
        return (
            <Layout pageTitle="Learner's Personalised Learning">
                <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center">
                    <div className="text-center">
                        <Brain className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Child Selected</h2>
                        <p className="text-gray-600">Please select a child to view their personalized learning activities.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Personalised Learning">
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Personalised Learning</h1>
                                <p className="text-gray-600">Customized MCQs and oral assessments for {selectedChild?.name}</p>
                            </div>
                            
                            {/* Child Selector */}
                            {children.length > 1 && (
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-purple-50 border border-purple-200 text-purple-800 px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                                    >
                                        {children.map(child => (
                                            <option key={child.student_id} value={child.student_id}>
                                                {child.name} - Grade {child.grade || 'N/A'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Tabs */}
                        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setActiveTab('mcqs')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    activeTab === 'mcqs'
                                        ? 'bg-white text-purple-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                <BookOpen className="w-4 h-4 inline mr-2" />
                                MCQ Assessments
                            </button>
                            <button
                                onClick={() => setActiveTab('oral')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    activeTab === 'oral'
                                        ? 'bg-white text-purple-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                <Headphones className="w-4 h-4 inline mr-2" />
                                Oral Assessments
                            </button>
                        </div>
                    </div>

                    {learningData && (
                        <>
                            {/* MCQ Assessments Tab */}
                            {activeTab === 'mcqs' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                    {learningData.mcqs.map((mcq) => (
                                        <div key={mcq.id} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                                            {/* Header */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                                                        <BookOpen className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-bold text-gray-800 line-clamp-2">{mcq.title}</h3>
                                                        <p className="text-sm text-gray-600">{mcq.subject}</p>
                                                    </div>
                                                </div>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(mcq.status)}`}>
                                                    {mcq.status}
                                                </span>
                                            </div>

                                            {/* Details */}
                                            <div className="space-y-3 mb-4">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-600">Questions:</span>
                                                    <span className="font-medium">{mcq.questions}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-600">Time Limit:</span>
                                                    <span className="font-medium">{mcq.timeLimit}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-600">Difficulty:</span>
                                                    <span className={`px-2 py-1 ${getDifficultyColor(mcq.difficulty)} text-white text-xs rounded-full`}>
                                                        {mcq.difficulty}
                                                    </span>
                                                </div>
                                                {mcq.lastScore && (
                                                    <div className="flex items-center justify-between text-sm">
                                                        <span className="text-gray-600">Last Score:</span>
                                                        <span className="font-bold text-green-600">{mcq.lastScore}%</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Recommendation */}
                                            <div className="bg-purple-50 rounded-lg p-3 mb-4">
                                                <h4 className="text-sm font-semibold text-purple-800 mb-1">Recommended for:</h4>
                                                <p className="text-sm text-purple-700">{mcq.recommendedFor}</p>
                                            </div>

                                            {/* Action Button */}
                                            <button className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2">
                                                {mcq.status === 'completed' ? (
                                                    <>
                                                        <CheckCircle className="w-4 h-4" />
                                                        Review Results
                                                    </>
                                                ) : (
                                                    <>
                                                        <Play className="w-4 h-4" />
                                                        Start Assessment
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Oral Assessments Tab */}
                            {activeTab === 'oral' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                    {learningData.oralAssessments.map((oral) => (
                                        <div key={oral.id} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                                            {/* Header */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                                                        <Headphones className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-bold text-gray-800 line-clamp-2">{oral.title}</h3>
                                                        <p className="text-sm text-gray-600">{oral.subject}</p>
                                                    </div>
                                                </div>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(oral.status)}`}>
                                                    {oral.status}
                                                </span>
                                            </div>

                                            {/* Details */}
                                            <div className="space-y-3 mb-4">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-600">Duration:</span>
                                                    <span className="font-medium">{oral.duration}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-600">Attempts:</span>
                                                    <span className="font-medium">{oral.attempts}</span>
                                                </div>
                                                {oral.lastScore && (
                                                    <div className="flex items-center justify-between text-sm">
                                                        <span className="text-gray-600">Last Score:</span>
                                                        <span className="font-bold text-green-600">{oral.lastScore}%</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Skills */}
                                            <div className="mb-4">
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Skills Assessed:</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {oral.skills.map((skill, index) => (
                                                        <span 
                                                            key={index}
                                                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                                        >
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <button className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2">
                                                {oral.status === 'completed' ? (
                                                    <>
                                                        <CheckCircle className="w-4 h-4" />
                                                        Listen to Recording
                                                    </>
                                                ) : (
                                                    <>
                                                        <Play className="w-4 h-4" />
                                                        Start Recording
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Recommendations */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-6">
                                    <Star className="w-8 h-8 text-purple-600 mr-3" />
                                    <h3 className="text-2xl font-bold text-gray-800">Personalized Recommendations</h3>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Strength Areas */}
                                    <div className="bg-green-50 rounded-lg p-4">
                                        <h4 className="font-bold text-green-800 mb-3 flex items-center">
                                            <CheckCircle className="w-5 h-5 mr-2" />
                                            Strength Areas
                                        </h4>
                                        <ul className="space-y-2">
                                            {learningData.recommendations.strengthAreas.map((area, index) => (
                                                <li key={index} className="text-sm text-green-700 flex items-start">
                                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                                    {area}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Improvement Areas */}
                                    <div className="bg-yellow-50 rounded-lg p-4">
                                        <h4 className="font-bold text-yellow-800 mb-3 flex items-center">
                                            <Clock className="w-5 h-5 mr-2" />
                                            Focus Areas
                                        </h4>
                                        <ul className="space-y-2">
                                            {learningData.recommendations.improvementAreas.map((area, index) => (
                                                <li key={index} className="text-sm text-yellow-700 flex items-start">
                                                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                                    {area}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Suggested Activities */}
                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                                            <Brain className="w-5 h-5 mr-2" />
                                            Suggested Activities
                                        </h4>
                                        <ul className="space-y-2">
                                            {learningData.recommendations.suggestedActivities.map((activity, index) => (
                                                <li key={index} className="text-sm text-blue-700 flex items-start">
                                                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                                    {activity}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default PersonalisedLearning;